import React, { useState } from 'react';
import {
  Button,
  Card,
  Col,
  Descriptions,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type {
  ControlledDocumentChangeRequest,
  ControlledDocumentDistribution,
  ControlledDocumentRevision,
} from '@/services/documentControl.service';
import {
  ArrowLeftOutlined,
  CheckOutlined,
  EditOutlined,
  RollbackOutlined,
  SendOutlined,
} from '@ant-design/icons';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import documentControlService from '@/services/documentControl.service';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';
import { ENTITY_TYPE_CONTROLLED_DOCUMENT } from '@/constants/approval-entity-types';
import ProcurementAttachmentsCard from '@/components/procurement/ProcurementAttachmentsCard';
import { DOCUMENT_ENTITY_TYPE_CONTROLLED_DOCUMENT } from '@/constants/document-entity-types';
import { useAuthStore } from '@/store/authStore';
import { apiErrorMessage } from '@/utils/apiErrorMessage';

const { Title, Text } = Typography;
const { TextArea } = Input;

const statusColor = (status: string) => {
  const map: Record<string, string> = {
    Draft: 'default',
    'Pending Approval': 'processing',
    Issued: 'success',
    Obsolete: 'error',
    Superseded: 'warning',
  };
  return map[status] || 'default';
};

const DocumentManagementDetailPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const perms = (useAuthStore((s) => s.user?.permissions) ?? []) as string[];
  const canEdit =
    perms.includes('quality.document_control.edit') ||
    perms.includes('quality.document_control.create') ||
    perms.length === 0;
  const canSubmit =
    perms.includes('quality.document_control.submit') ||
    perms.includes('quality.document_control.edit') ||
    perms.length === 0;
  const canDistribute =
    perms.includes('quality.document_control.distribute') ||
    perms.includes('quality.document_control.edit') ||
    perms.length === 0;
  const canObsolete =
    perms.includes('quality.document_control.obsolete') ||
    perms.includes('quality.document_control.edit') ||
    perms.length === 0;

  const [distForm] = Form.useForm();
  const [crForm] = Form.useForm();
  const [reviseForm] = Form.useForm();
  const [obsoleteForm] = Form.useForm();
  const [distOpen, setDistOpen] = useState(false);
  const [crOpen, setCrOpen] = useState(false);
  const [reviseOpen, setReviseOpen] = useState(false);
  const [obsoleteOpen, setObsoleteOpen] = useState(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['document-control', id],
    queryFn: () => documentControlService.get(id!),
    enabled: !!id,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['document-control'] });
    qc.invalidateQueries({ queryKey: ['document-control', id] });
  };

  const submitMut = useMutation({
    mutationFn: () => documentControlService.submit(id!),
    onSuccess: () => {
      message.success('Submitted for approval');
      invalidate();
    },
    onError: (e) => message.error(apiErrorMessage(e)),
  });

  const externalCheckMut = useMutation({
    mutationFn: () => documentControlService.externalCheck(id!),
    onSuccess: () => {
      message.success('External review recorded (+3 months)');
      invalidate();
    },
    onError: (e) => message.error(apiErrorMessage(e)),
  });

  const distMut = useMutation({
    mutationFn: (values: any) =>
      documentControlService.distribute(id!, [
        {
          recipient_name: values.recipient_name,
          recipient_department: values.recipient_department || null,
          recipient_user_id: values.recipient_user_id || null,
          copy_type: values.copy_type || 'SOFT',
        },
      ]),
    onSuccess: () => {
      message.success('Distributed');
      setDistOpen(false);
      distForm.resetFields();
      invalidate();
    },
    onError: (e) => message.error(apiErrorMessage(e)),
  });

  const recallMut = useMutation({
    mutationFn: (distributionId: string) => documentControlService.recall(id!, distributionId),
    onSuccess: () => {
      message.success('Recalled');
      invalidate();
    },
    onError: (e) => message.error(apiErrorMessage(e)),
  });

  const ackMut = useMutation({
    mutationFn: (distributionId: string) => documentControlService.acknowledge(id!, distributionId),
    onSuccess: () => {
      message.success('Acknowledged');
      invalidate();
    },
    onError: (e) => message.error(apiErrorMessage(e)),
  });

  const crMut = useMutation({
    mutationFn: (values: any) =>
      documentControlService.createChangeRequest(id!, {
        requester_name: values.requester_name,
        description: values.description,
        modification_reason: values.modification_reason || null,
        impact_assessment: values.impact_assessment || null,
        risk_notes: values.risk_notes || null,
        moc_required: !!values.moc_required,
        moc_id: values.moc_id || null,
      }),
    onSuccess: () => {
      message.success('Change request submitted');
      setCrOpen(false);
      crForm.resetFields();
      invalidate();
    },
    onError: (e) => message.error(apiErrorMessage(e)),
  });

  const reviewCrMut = useMutation({
    mutationFn: (p: { crId: string; decision: 'Approved' | 'Rejected' | 'Under Review' }) =>
      documentControlService.reviewChangeRequest(id!, p.crId, { decision: p.decision }),
    onSuccess: () => {
      message.success('Change request updated');
      invalidate();
    },
    onError: (e) => message.error(apiErrorMessage(e)),
  });

  const reviseMut = useMutation({
    mutationFn: (values: any) =>
      documentControlService.revise(id!, {
        nature_of_amendment: values.nature_of_amendment,
        change_request_id: values.change_request_id || null,
        moc_id: values.moc_id || null,
        moc_ref: values.moc_ref || null,
      }),
    onSuccess: () => {
      message.success('New revision started (Draft) — edit content then submit for approval');
      setReviseOpen(false);
      reviseForm.resetFields();
      invalidate();
    },
    onError: (e) => message.error(apiErrorMessage(e)),
  });

  const obsoleteMut = useMutation({
    mutationFn: (values: any) =>
      documentControlService.obsolete(id!, {
        obsolete_reason: values.obsolete_reason || null,
        retain_for_reference: !!values.retain_for_reference,
        retain_reason: values.retain_reason || null,
      }),
    onSuccess: () => {
      message.success('Marked obsolete; active copies recalled');
      setObsoleteOpen(false);
      obsoleteForm.resetFields();
      invalidate();
    },
    onError: (e) => message.error(apiErrorMessage(e)),
  });

  if (!id) return null;

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/qms/document-management')}>
            Back
          </Button>
          <div>
            <Title level={3} style={{ margin: 0 }}>
              {data?.document_number || 'Controlled document'}
            </Title>
            <Text type="secondary">{data?.title}</Text>
          </div>
          {data ? <Tag color={statusColor(data.status)}>{data.status}</Tag> : null}
          {data?.status === 'Issued' && Number(data.requires_controlled_stamp) ? (
            <Tag color="blue">CONTROLLED COPY</Tag>
          ) : null}
          {data?.status === 'Obsolete' ? (
            <Tag color="red">
              {Number(data.retain_for_reference) ? 'OBSOLETE COPY (retained)' : 'OBSOLETE COPY'}
            </Tag>
          ) : null}
        </Space>
        <Space wrap>
          {canEdit && data?.status === 'Draft' ? (
            <Button icon={<EditOutlined />} onClick={() => navigate(`/qms/document-management/${id}/edit`)}>
              Edit
            </Button>
          ) : null}
          {canSubmit && data?.status === 'Draft' ? (
            <Button
              type="primary"
              icon={<SendOutlined />}
              loading={submitMut.isPending}
              onClick={() => submitMut.mutate()}
            >
              Submit for approval
            </Button>
          ) : null}
          {canEdit && data?.status === 'Issued' ? (
            <Button onClick={() => setReviseOpen(true)}>Start revision</Button>
          ) : null}
          {canDistribute && data?.status === 'Issued' ? (
            <Button onClick={() => setDistOpen(true)}>Distribute</Button>
          ) : null}
          {canEdit && data?.origin === 'EXTERNAL' && data.status === 'Issued' ? (
            <Button loading={externalCheckMut.isPending} onClick={() => externalCheckMut.mutate()}>
              Record external check
            </Button>
          ) : null}
          {canObsolete && (data?.status === 'Issued' || data?.status === 'Draft') ? (
            <Button danger onClick={() => setObsoleteOpen(true)}>
              Mark obsolete
            </Button>
          ) : null}
          {canEdit && data?.status === 'Issued' ? (
            <Button onClick={() => setCrOpen(true)}>Change request</Button>
          ) : null}
        </Space>
      </Space>

      <Card loading={isLoading} style={{ marginBottom: 16 }}>
        <Descriptions column={{ xs: 1, md: 2, lg: 3 }} size="small">
          <Descriptions.Item label="Origin">{data?.origin}</Descriptions.Item>
          <Descriptions.Item label="Level">{data?.document_level || '—'}</Descriptions.Item>
          <Descriptions.Item label="Family">{data?.doc_family}</Descriptions.Item>
          <Descriptions.Item label="Revision">{data?.revision}</Descriptions.Item>
          <Descriptions.Item label="Revision date">
            {data?.revision_date ? dayjs(data.revision_date).format('DD/MM/YYYY') : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Effective">
            {data?.effective_date ? dayjs(data.effective_date).format('DD/MM/YYYY') : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Department">
            {data?.department_name
              ? `${data.finance_department_code || data.department_code || ''} — ${data.department_name}`.replace(
                  /^ — /,
                  ''
                )
              : data?.department_code || '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Custodian">
            {data?.custodian_name || '—'}
            {data?.custodian_location ? ` @ ${data.custodian_location}` : ''}
          </Descriptions.Item>
          <Descriptions.Item label="Prepared by">
            {[data?.prepared_by_name, data?.prepared_by_designation].filter(Boolean).join(' — ') || '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Language">{data?.language || '—'}</Descriptions.Item>
          <Descriptions.Item label="Next review">
            {data?.next_review_date ? dayjs(data.next_review_date).format('DD/MM/YYYY') : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="MOC">
            {data?.moc_id ? (
              <Link to={`/grc/moc/${data.moc_id}`}>{data.moc_ref_no || data.moc_id}</Link>
            ) : (
              '—'
            )}
          </Descriptions.Item>
          {data?.origin === 'EXTERNAL' ? (
            <Descriptions.Item label="Publisher / source" span={2}>
              {data.publisher_or_source || '—'}
            </Descriptions.Item>
          ) : null}
          <Descriptions.Item label="Description" span={3}>
            {data?.description || '—'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Tabs
        items={[
          {
            key: 'revisions',
            label: 'Amendments / revisions',
            children: (
              <Table<ControlledDocumentRevision>
                rowKey="id"
                size="small"
                pagination={false}
                dataSource={data?.revisions || []}
                columns={
                  [
                    { title: 'Rev', dataIndex: 'revision', width: 80 },
                    {
                      title: 'Date',
                      dataIndex: 'revision_date',
                      width: 120,
                      render: (v) => (v ? dayjs(v).format('DD/MM/YYYY') : '—'),
                    },
                    { title: 'Nature of amendment', dataIndex: 'nature_of_amendment', ellipsis: true },
                    { title: 'MOC Ref', dataIndex: 'moc_ref', width: 120, render: (v) => v || '—' },
                    {
                      title: 'Current',
                      dataIndex: 'is_current',
                      width: 90,
                      render: (v) => (Number(v) ? <Tag color="green">Yes</Tag> : '—'),
                    },
                  ] as ColumnsType<ControlledDocumentRevision>
                }
              />
            ),
          },
          {
            key: 'distribution',
            label: 'Distribution',
            children: (
              <Table<ControlledDocumentDistribution>
                rowKey="id"
                size="small"
                dataSource={data?.distributions || []}
                columns={
                  [
                    { title: 'Recipient', dataIndex: 'recipient_name' },
                    {
                      title: 'Dept',
                      dataIndex: 'recipient_department',
                      width: 140,
                      render: (v) => v || '—',
                    },
                    { title: 'Copy', dataIndex: 'copy_type', width: 80 },
                    { title: 'Rev', dataIndex: 'issued_revision', width: 70 },
                    {
                      title: 'Issued',
                      dataIndex: 'issued_at',
                      width: 150,
                      render: (v) => (v ? dayjs(v).format('DD/MM/YYYY HH:mm') : '—'),
                    },
                    {
                      title: 'Status',
                      dataIndex: 'status',
                      width: 120,
                      render: (v) => <Tag>{v}</Tag>,
                    },
                    {
                      title: 'Actions',
                      key: 'actions',
                      width: 200,
                      render: (_v, r) => (
                        <Space>
                          {r.status === 'Issued' ? (
                            <Button size="small" icon={<CheckOutlined />} onClick={() => ackMut.mutate(r.id)}>
                              Ack
                            </Button>
                          ) : null}
                          {r.status !== 'Recalled' && canDistribute ? (
                            <Button
                              size="small"
                              danger
                              icon={<RollbackOutlined />}
                              onClick={() => recallMut.mutate(r.id)}
                            >
                              Recall
                            </Button>
                          ) : null}
                        </Space>
                      ),
                    },
                  ] as ColumnsType<ControlledDocumentDistribution>
                }
              />
            ),
          },
          {
            key: 'changes',
            label: 'Change requests',
            children: (
              <Table<ControlledDocumentChangeRequest>
                rowKey="id"
                size="small"
                dataSource={data?.change_requests || []}
                columns={
                  [
                    { title: 'CR No', dataIndex: 'cr_number', width: 120 },
                    { title: 'Requester', dataIndex: 'requester_name', width: 140 },
                    { title: 'Description', dataIndex: 'description', ellipsis: true },
                    {
                      title: 'Status',
                      dataIndex: 'status',
                      width: 120,
                      render: (v) => <Tag>{v}</Tag>,
                    },
                    {
                      title: 'Actions',
                      key: 'actions',
                      width: 220,
                      render: (_v, r) =>
                        canEdit && (r.status === 'Submitted' || r.status === 'Under Review') ? (
                          <Space>
                            <Button
                              size="small"
                              type="primary"
                              onClick={() => reviewCrMut.mutate({ crId: r.id, decision: 'Approved' })}
                            >
                              Approve
                            </Button>
                            <Button
                              size="small"
                              danger
                              onClick={() => reviewCrMut.mutate({ crId: r.id, decision: 'Rejected' })}
                            >
                              Reject
                            </Button>
                          </Space>
                        ) : (
                          '—'
                        ),
                    },
                  ] as ColumnsType<ControlledDocumentChangeRequest>
                }
              />
            ),
          },
          {
            key: 'approvals',
            label: 'Approvals',
            children: (
              <Card>
                {(data?.status === 'Pending Approval' || data?.approval_instance_id) && (
                  <ApprovalPanel
                    entityType={ENTITY_TYPE_CONTROLLED_DOCUMENT}
                    entityId={data!.id}
                    extraInvalidateQueryKeys={[['document-control'], ['document-control', id]]}
                  />
                )}
                {data?.status !== 'Pending Approval' && !data?.approval_instance_id ? (
                  <Text type="secondary">No active approval instance.</Text>
                ) : null}
              </Card>
            ),
          },
          {
            key: 'attachments',
            label: 'Attachments',
            children: (
              <ProcurementAttachmentsCard
                entityType={DOCUMENT_ENTITY_TYPE_CONTROLLED_DOCUMENT}
                entityId={data?.id || id}
                queryKeyPrefix={['documents', 'controlled-document']}
              />
            ),
          },
        ]}
      />

      <Modal
        title="Distribute controlled copy"
        open={distOpen}
        onCancel={() => setDistOpen(false)}
        onOk={() => distForm.submit()}
        confirmLoading={distMut.isPending}
        destroyOnClose
      >
        <Form form={distForm} layout="vertical" onFinish={(v) => distMut.mutate(v)}>
          <Form.Item name="recipient_name" label="Recipient name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="recipient_department" label="Department">
            <Input />
          </Form.Item>
          <Form.Item name="recipient_user_id" label="Recipient user ID (optional, for notification)">
            <Input placeholder="User UUID" />
          </Form.Item>
          <Form.Item name="copy_type" label="Copy type" initialValue="SOFT">
            <Select
              options={[
                { value: 'SOFT', label: 'Soft copy (read-only)' },
                { value: 'HARD', label: 'Hard copy (CONTROLLED COPY stamp)' },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Document change request"
        open={crOpen}
        onCancel={() => setCrOpen(false)}
        onOk={() => crForm.submit()}
        confirmLoading={crMut.isPending}
        width={640}
        destroyOnClose
      >
        <Form form={crForm} layout="vertical" onFinish={(v) => crMut.mutate(v)}>
          <Form.Item name="requester_name" label="Requester" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Change description" rules={[{ required: true }]}>
            <TextArea rows={3} />
          </Form.Item>
          <Form.Item name="modification_reason" label="Modification reason">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item name="impact_assessment" label="Impact on other processes">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item name="risk_notes" label="Risks associated with change">
            <TextArea rows={2} />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="moc_required" label="MOC required?" initialValue={false}>
                <Select
                  options={[
                    { value: false, label: 'No' },
                    { value: true, label: 'Yes' },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="moc_id" label="MOC ID">
                <Input />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      <Modal
        title="Start new revision"
        open={reviseOpen}
        onCancel={() => setReviseOpen(false)}
        onOk={() => reviseForm.submit()}
        confirmLoading={reviseMut.isPending}
        destroyOnClose
      >
        <Form form={reviseForm} layout="vertical" onFinish={(v) => reviseMut.mutate(v)}>
          <Form.Item
            name="nature_of_amendment"
            label="Nature of amendment"
            rules={[{ required: true }]}
            extra="Recorded on the Amendments Sheet / revision history."
          >
            <TextArea rows={3} />
          </Form.Item>
          <Form.Item name="change_request_id" label="Approved change request (optional)">
            <Select
              allowClear
              options={(data?.change_requests || [])
                .filter((c) => c.status === 'Approved')
                .map((c) => ({ value: c.id, label: `${c.cr_number} — ${c.description}` }))}
            />
          </Form.Item>
          <Form.Item name="moc_ref" label="MOC No. Ref.">
            <Input />
          </Form.Item>
          <Form.Item name="moc_id" label="MOC ID">
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Mark obsolete"
        open={obsoleteOpen}
        onCancel={() => setObsoleteOpen(false)}
        onOk={() => obsoleteForm.submit()}
        confirmLoading={obsoleteMut.isPending}
        okButtonProps={{ danger: true }}
        destroyOnClose
      >
        <Form form={obsoleteForm} layout="vertical" onFinish={(v) => obsoleteMut.mutate(v)}>
          <Form.Item name="obsolete_reason" label="Reason">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item name="retain_for_reference" label="Retain for legal / knowledge?" initialValue={false}>
            <Select
              options={[
                { value: false, label: 'No — discard / move to obsolete folder' },
                { value: true, label: 'Yes — stamp OBSOLETE COPY and retain' },
              ]}
            />
          </Form.Item>
          <Form.Item name="retain_reason" label="Retention reason">
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default DocumentManagementDetailPage;
